import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    HostListener,
    Inject,
    Input,
    OnDestroy,
    Optional,
    Renderer2,
    ViewChild,
} from '@angular/core';
import { ISeezFinanceCalculator } from '@ncg/data';
import { TranslateModule } from '@ngx-translate/core';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { firstValueFrom } from 'rxjs';
import { FeatureDetectionService } from '../../../core/feature-detection.service';
import { SettingsService } from '../../../core/settings.service';
import { TrackingService } from '../../../core/tracking.service';
import { ImpactOverlayRef } from '../../../overlay/overlay-ref';
import { IMPACT_OVERLAY_DATA } from '../../../overlay/overlay.tokens';
import { UtilsModule } from '../../../utils/utils.module';
import { SeezBuyButtonComponent } from '../product-buttons/seez-buy-button.component';

// To simplify async loading, we have this helper component that initializes autoProff.
// By having a component do it, we simply render this alongside any related codesnippet. That way the template controls when to load.
@Component({
    selector: 'ncg-seez-finance-calculator',
    standalone: true,
    template: `
        <section [class.visible]="showComponent" [class.popup]="isOverlay" [attr.role]="isOverlay ? 'dialog' : undefined">
            <header *ngIf="isOverlay">
                <button type="button" class="close-button" (click)="onClose()" aria-label="closes overlay">
                    <svg-icon-sprite
                        [src]="'close_light'"
                        [viewBox]="'0 0 30 30'"
                        [width]="'30px'"
                        [height]="'30px'"
                        aria-hidden="true"
                        class="is-flex"
                        classes=""
                    ></svg-icon-sprite>
                </button>
            </header>
            <div *ngIf="preparedData?.infoText" [innerHTML]="preparedData?.infoText | safe: 'html'"></div>
            <div #container></div>
            <ncg-seez-buy-button
                [buttonClass]="'button is-primary is-centered apply-button'"
                [textOverride]="preparedData?.applyButtonText || ('product_details.finance_calculator_apply_now_button' | translate)"
                [productId]="preparedData?.productId"
            ></ncg-seez-buy-button>
        </section>
    `,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [CommonModule, UtilsModule, TranslateModule, SeezBuyButtonComponent, IconSpriteModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrls: ['./seez-finance-calculator.component.scss'],
})
export class SeezFinanceCalculatorComponent implements AfterViewInit, OnDestroy {
    private observer?: MutationObserver;
    private currentTerm?: string;

    public showComponent?: boolean;
    public isOverlay?: boolean;
    public preparedData?: ISeezFinanceCalculator;

    @Input()
    public data?: ISeezFinanceCalculator;

    @ViewChild('container')
    public readonly container?: ElementRef<HTMLDivElement>;

    constructor(
        private readonly cd: ChangeDetectorRef,
        private readonly renderer: Renderer2,
        private readonly el: ElementRef,
        private readonly settingsService: SettingsService,
        private readonly featureDetectionService: FeatureDetectionService,
        private readonly trackingService: TrackingService,
        @Optional()
        private readonly dialogRef: ImpactOverlayRef,
        @Optional()
        @Inject(IMPACT_OVERLAY_DATA)
        private readonly overlayData: ISeezFinanceCalculator
    ) {}

    public ngOnDestroy(): void {
        this.removeTracking().catch(console.error);
    }

    public ngAfterViewInit() {
        if (this.featureDetectionService.isBrowser()) {
            this.prepareData();
            this.insertFinanceCalculator().catch(console.error);
        }
    }

    @HostListener('document:keydown.esc', ['$event'])
    public onClose(): void {
        if (this.dialogRef) {
            this.dialogRef.close();
        }
    }

    private prepareData() {
        if (this.data) {
            this.preparedData = this.data;
        } else if (this.overlayData) {
            this.isOverlay = true;
            this.preparedData = this.overlayData;
        }
    }

    private async insertFinanceCalculator() {
        const container = this.container?.nativeElement;
        const seezListingId = this.preparedData?.listingId;
        if (seezListingId && container) {
            this.showComponent = true;
            const { currency, seoLanguage } = await firstValueFrom(this.settingsService.get());
            const seezComponent = this.renderer.createElement('seez-sdk-loan-calculator');
            this.renderer.setAttribute(seezComponent, 'listing-id', seezListingId);
            this.renderer.setAttribute(seezComponent, 'currency', currency || 'DKK');
            this.renderer.setAttribute(seezComponent, 'lg', seoLanguage || 'da');
            this.renderer.setAttribute(seezComponent, 'show-calculation-details', 'true');
            this.renderer.appendChild(container, seezComponent);
            this.applyTracking().catch(console.error);
        }
        this.cd.detectChanges();
    }

    private async applyTracking() {
        const trackingElements = await this.getTrackingElements(true);
        if (trackingElements) {
            trackingElements.showDetailsButton.addEventListener('click', this.onShowDetailsClick);
            trackingElements.applyNowButton.addEventListener('click', this.onApplyNowClick);
            trackingElements.downPaymentInput.addEventListener('change', this.onDownPaymentChange);
            this.currentTerm = this.getCurrentTerm(trackingElements.paymentTermButtons);
            trackingElements.paymentTermButtons.forEach(() => {
                addEventListener('click', this.onPaymentTermChange);
            });
        }
    }

    private async removeTracking() {
        this.observer?.disconnect();
        const trackingElements = await this.getTrackingElements(false);
        if (trackingElements) {
            trackingElements.showDetailsButton.removeEventListener('click', this.onShowDetailsClick);
            trackingElements.applyNowButton.removeEventListener('click', this.onApplyNowClick);
            trackingElements.downPaymentInput.removeEventListener('change', this.onDownPaymentChange);
            trackingElements.paymentTermButtons.forEach(() => {
                removeEventListener('click', this.onPaymentTermChange);
            });
        }
    }

    private async getTrackingElements(awaitMutations: boolean) {
        const element = this.el?.nativeElement;
        if (element) {
            const shadowDom = element.querySelector('seez-sdk-loan-calculator')?.shadowRoot;
            if (shadowDom) {
                const applyNowButton: HTMLButtonElement = element.querySelector('ncg-seez-buy-button a');
                const downPaymentInput: HTMLInputElement = shadowDom.querySelector('.downPayment input');
                let paymentTermButtons: NodeListOf<HTMLButtonElement> = shadowDom.querySelectorAll('.paymentTerms button');

                if (!paymentTermButtons?.length && awaitMutations) {
                    const paymentTermButtonsContainer: HTMLDivElement = shadowDom.querySelector('.paymentTerms');
                    if (paymentTermButtonsContainer) {
                        paymentTermButtons = await new Promise<NodeListOf<HTMLButtonElement>>((resolve) => {
                            this.observer = new MutationObserver(() => {
                                const buttons: NodeListOf<HTMLButtonElement> = shadowDom.querySelectorAll('.paymentTerms button');
                                if (buttons.length) {
                                    this.observer?.disconnect();
                                    resolve(buttons);
                                }
                            });

                            this.observer.observe(paymentTermButtonsContainer, { childList: true, subtree: true });
                        });
                    }
                }

                const showDetailsButton: HTMLButtonElement = shadowDom.querySelector('.loanDetailsAccordionHeader button');
                if (applyNowButton && downPaymentInput && paymentTermButtons && showDetailsButton) {
                    return {
                        downPaymentInput,
                        paymentTermButtons: Array.from(paymentTermButtons),
                        showDetailsButton,
                        applyNowButton,
                    };
                }
            }
        }
        return undefined;
    }

    private getCurrentTerm(paymentTermButtons: HTMLButtonElement[]): string | undefined {
        const selectedButton = paymentTermButtons.find((btn) => btn.classList.contains('selected'));
        return selectedButton?.getAttribute('data-test-id') || undefined;
    }

    private readonly onDownPaymentChange = (e: Event) => {
        const target = e.target as HTMLInputElement;
        this.trackingService.trackSeez('down payment change', target.value);
    };

    private readonly onPaymentTermChange = () => {
        this.getTrackingElements(false)
            .then((trackingElements) => {
                if (trackingElements) {
                    const oldTerm = this.currentTerm;
                    const newTerm = this.getCurrentTerm(trackingElements.paymentTermButtons);
                    if (oldTerm !== newTerm) {
                        this.trackingService.trackSeez('payment term change', newTerm);
                        this.currentTerm = newTerm;
                    }
                }
            })
            .catch(console.error);
    };

    private readonly onShowDetailsClick = (e: Event) => {
        const target = e.target as HTMLButtonElement;
        setTimeout(() => {
            const isVisible = Boolean(target?.closest('.loanDetailsAccordion')?.querySelector('.loanDetailsTable'));
            this.trackingService.trackSeez('show details click', isVisible ? 'opened' : 'closed');
        }, 500);
    };

    private readonly onApplyNowClick = () => {
        this.trackingService.trackSeez('apply now click');
    };
}
